import { motion } from "framer-motion";
import { useEffect } from "react";
import { Calendar, Clock, Video, MessageSquare } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const BookingPage = () => {
  const bookingTypes = [
    {
      icon: <MessageSquare size={24} />,
      title: "Brief Consultation",
      duration: "15-20 minutes",
      description: "A complimentary introductory call to discuss your needs and explore how we might work together.",
      type: "Virtual"
    },
    {
      icon: <Video size={24} />,
      title: "One-on-One Coaching Session",
      duration: "60 minutes",
      description: "A personalized somatic coaching session tailored to your healing journey and goals.",
      type: "Virtual"
    },
    {
      icon: <Calendar size={24} />,
      title: "Speaking Engagement Inquiry",
      duration: "30 minutes",
      description: "Discuss your event needs, audience, and how my work might contribute to your conference or organization.",
      type: "Virtual"
    }
  ];

  // Load Calendly widget script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div data-testid="booking-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing" data-testid="booking-hero">
        <div className="section-container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.15 } }
            }}
            className="max-w-3xl"
          >
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-body text-xs uppercase tracking-[0.3em] text-brand-accent mb-4"
            >
              Book With Me
            </motion.p>
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-heading text-3xl sm:text-4xl md:text-5xl text-text-primary mb-6 sm:mb-8 leading-tight"
            >
              Begin Your{" "}
              <span className="italic text-brand-accent">Journey</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-body text-base sm:text-lg text-text-secondary leading-relaxed"
            >
              I welcome you to schedule a time to connect. Whether you're seeking 
              personal coaching, interested in community offerings, or exploring 
              institutional collaboration, the first step is a conversation.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Booking Types */}
      <section className="pb-12 sm:pb-16" data-testid="booking-types">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16"
          >
            {bookingTypes.map((booking, index) => (
              <motion.div
                key={booking.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card"
                data-testid={`booking-type-${index}`}
              >
                <div className="text-brand-accent mb-4">
                  {booking.icon}
                </div>
                <h3 className="font-heading text-lg sm:text-xl text-text-primary mb-2">
                  {booking.title}
                </h3>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-4 text-xs sm:text-sm text-text-secondary">
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {booking.duration}
                  </span>
                  <span className="text-brand-accent">{booking.type}</span>
                </div>
                <p className="font-body text-text-secondary text-sm leading-relaxed">
                  {booking.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Calendly Embed Section */}
      <section className="section-spacing bg-bg-secondary" data-testid="calendly-section">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="font-heading text-2xl sm:text-3xl text-text-primary mb-4">
              Schedule a Time
            </h2>
            <p className="font-body text-text-secondary max-w-2xl mx-auto text-sm sm:text-base">
              Select a time that works for you using the calendar below. 
              All sessions take place virtually via video call.
            </p>
          </motion.div>

          {/* Calendly Widget */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div 
              className="calendly-inline-widget rounded-sm overflow-hidden"
              data-url="https://calendly.com/inas-k-mahdi/somale-restoration-collective-intake-call?month=2026-11&background_color=242422&text_color=f5f5f0&primary_color=d4af37"
              data-testid="calendly-embed-container"
              style={{ minWidth: "320px", height: "700px" }}
            />
          </motion.div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-spacing" data-testid="what-to-expect">
        <div className="section-container">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-2xl sm:text-3xl text-text-primary mb-6 sm:mb-8">
                What to Expect
              </h2>

              <div className="space-y-6 sm:space-y-8">
                <div className="flex gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-8 h-8 bg-brand-accent text-text-dark flex items-center justify-center font-body font-semibold text-sm">
                    1
                  </div>
                  <div>
                    <h3 className="font-heading text-lg sm:text-xl text-text-primary mb-2">
                      Initial Connection
                    </h3>
                    <p className="font-body text-text-secondary leading-relaxed text-sm sm:text-base">
                      Our first conversation is about understanding your needs and intentions. 
                      There's no pressure to commit — this is simply a space to explore whether 
                      we're a good fit for working together.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-8 h-8 bg-brand-accent text-text-dark flex items-center justify-center font-body font-semibold text-sm">
                    2
                  </div>
                  <div>
                    <h3 className="font-heading text-lg sm:text-xl text-text-primary mb-2">
                      Consent-Centered Approach
                    </h3>
                    <p className="font-body text-text-secondary leading-relaxed text-sm sm:text-base">
                      Everything we do together is rooted in consent. You are always in control 
                      of your experience, and we move at the pace that feels right for your body 
                      and your healing.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-8 h-8 bg-brand-accent text-text-dark flex items-center justify-center font-body font-semibold text-sm">
                    3
                  </div>
                  <div>
                    <h3 className="font-heading text-lg sm:text-xl text-text-primary mb-2">
                      Confidentiality & Safety
                    </h3>
                    <p className="font-body text-text-secondary leading-relaxed text-sm sm:text-base">
                      Your privacy and safety are paramount. All sessions are confidential, 
                      and I maintain a trauma-responsive, non-judgmental space for whatever 
                      arises in our work together.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Alternative */}
      <section className="section-spacing bg-brand-deep-green" data-testid="contact-alternative">
        <div className="section-container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <p className="font-accent text-lg sm:text-xl text-text-primary/80 mb-4 sm:mb-6">
              "The journey of healing begins with a single step."
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl text-text-primary mb-4 sm:mb-6">
              Questions Before Booking?
            </h2>
            <p className="font-body text-text-secondary mb-6 sm:mb-8 text-sm sm:text-base">
              If you have questions about my offerings or want to discuss your specific 
              needs before scheduling, feel free to reach out through the consultation booking above.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
